<template>
  <v-container fluid class="pa-6 bg-light-gradient">
    <!-- Header Section -->
    <v-row align="center" class="mb-10">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-icon large color="primary" class="mr-3">mdi-school</v-icon>
          <h1 class="text-h3 font-weight-black primary--text tracking-tight" data-v-step="0">
            {{ $t('attendance_list') }}
          </h1>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" rounded @click="$tours['attendanceTour'].start()">
            <v-icon left>mdi-help-circle-outline</v-icon>
            Ver Tutorial
          </v-btn>
        </div>
        <p class="text-subtitle-1 grey--text text--darken-2 font-weight-medium">
          Selecciona un salón para pasar lista o consultar los estudiantes asignados.
        </p>
        <v-divider class="custom-divider"></v-divider>
      </v-col>
    </v-row>

    <!-- Classrooms Grid -->
    <v-row v-if="loadingClassrooms" justify="center" class="my-12">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="74"
          width="6"
        ></v-progress-circular>
        <p class="mt-6 text-h6 primary--text font-weight-medium animate-pulse">Cargando salones...</p>
      </v-col>
    </v-row>

    <v-row v-else-if="classrooms.length === 0" justify="center" class="my-12">
      <v-col cols="12" sm="8" md="6" class="text-center">
        <div class="empty-state-container pa-8 rounded-xl elevation-2 bg-white">
          <v-icon size="120" color="grey lighten-3">mdi-google-classroom</v-icon>
          <h2 class="text-h4 grey--text text--darken-1 mt-6 font-weight-bold">
            Sin salones asignados
          </h2>
          <p class="text-body-1 grey--text mt-2">
            No tienes salones de clases asignados en este momento.
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row v-else class="classrooms-grid">
      <v-col
        v-for="(classroom, index) in classrooms"
        :key="classroom.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="pa-4"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            :elevation="hover ? 16 : 4"
            class="classroom-card rounded-xl overflow-hidden transition-all-slow"
            :class="{'on-hover': hover}"
            @click="showStudents(classroom)"
          >
            <!-- Header Gradient -->
            <div :class="['card-header-gradient', getGradientClass(index)]">
              <v-icon dark class="classroom-icon-floating">mdi-human-male-board</v-icon>
            </div>
            
            <v-card-text class="pt-8 px-6 pb-4">
              <div class="overline primary--text font-weight-black mb-1">
                {{ classroom.grade_name || 'Grado' }}
              </div>
              <h2 class="text-h5 font-weight-bold text-dark mb-4">
                {{ classroom.name || 'Salón Sin Nombre' }}
              </h2>
              
              <v-chip
                small
                outlined
                color="primary"
                class="font-weight-bold"
              >
                <v-icon left x-small>mdi-account-multiple</v-icon>
                {{ $t('estudiantes') }}
              </v-chip>
            </v-card-text>
            
            <v-divider class="mx-6 opacity-10"></v-divider>
            
            <v-card-actions class="px-6 py-4 d-flex flex-column align-stretch">
              <v-btn
                block
                color="primary"
                class="mb-3 rounded-lg font-weight-bold"
                elevation="2"
                @click.stop="showStudents(classroom)"
                :data-v-step="index === 0 ? '1' : ''"
              >
                <v-icon left>mdi-account-check</v-icon>
                {{ $t('attendance_list') }}
              </v-btn>
              
              <v-btn
                block
                outlined
                color="orange darken-3"
                class="rounded-lg font-weight-bold"
                @click.stop="transferClassroom(classroom)"
                :data-v-step="index === 0 ? '2' : ''"
              >
                <v-icon left>mdi-account-switch</v-icon>
                {{ $t('transfer') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <!-- Students Dialog -->
    <v-dialog
      v-model="dialogStudents"
      max-width="850px"
      scrollable
      transition="scale-transition"
    >
      <v-card class="student-list-card glass-card">
        <v-toolbar flat dark color="primary" class="gradient-toolbar px-4">
          <v-avatar color="white" size="40" class="mr-4">
            <v-icon color="primary">mdi-account-group</v-icon>
          </v-avatar>
          <v-toolbar-title class="font-weight-black text-h5 tracking-tight">
            {{ selectedClassroom ? selectedClassroom.name : 'Estudiantes' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- <v-btn 
            v-if="selectedStudents.length > 0"
            color="success" 
            dark 
            depressed
            class="mr-4 rounded-lg font-weight-bold"
            :loading="loadingBulk"
            @click="markBulkAttendance"
          >
            <v-icon left>mdi-account-check</v-icon>
            Pasar lista masivo ({{ selectedStudents.length }})
          </v-btn> -->
          <template v-if="selectedStudents.length > 0" v-slot:extension>
              <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                      <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              color="blue"
                              bottom
                              left
                              absolute
                              :loading="loadingBulk"
                              :disabled="selectedStudents.length === 0"
                              @click="markBulkAttendance"
                      >
                          <v-icon>mdi-content-save</v-icon>
                      </v-btn>
                  </template>
                  <span>{{$t('save_list')}}</span>
              </v-tooltip>
          </template>
          <v-btn icon @click="dialogStudents = false" class="bg-white-opacity">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <v-data-table
            v-model="selectedStudents"
            :headers="computedStudentHeaders"
            :items="students"
            :loading="loadingStudents"
            :show-select="showBulkSelection"
            item-key="id"
            class="elevation-0 custom-data-table"
            :no-data-text="$t('no_assigned_students')"
            loading-text="Cargando lista de estudiantes..."
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.avatar="{ item }">
              <v-avatar size="50" class="my-3 elevation-4 border-2-white shadow-sm">
                <v-img :src="item.avatar || defaultAvatar" :alt="item.first_name">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0 bg-grey-lighten-4" align="center" justify="center">
                      <v-icon color="grey lighten-1" size="30">mdi-account-outline</v-icon>
                    </v-row>
                  </template>
                </v-img>
              </v-avatar>
            </template>
            
            <template v-slot:item.first_name="{ item }">
              <div class="text-body-1 font-weight-bold text-darker">{{ item.first_name }}</div>
            </template>

            <template v-slot:item.last_name="{ item }">
              <div class="text-body-1 text-grey-darken-2">{{ item.last_name }}</div>
            </template>

            <template v-slot:item.status="{ item }">
              <div class="d-flex justify-center">
                <!-- Status -1: Botón para tomar asistencia -->
                <v-tooltip bottom v-if="item.status === -1">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      fab
                      x-small
                      color="red accent-2"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      elevation="3"
                      :loading="loadingAction === item.id"
                      @click="changeAttendanceStudent(true, item.id)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('add_assistance') }}</span>
                </v-tooltip>

                <!-- Status 0: Botón para quitar asistencia -->
                <v-tooltip bottom v-else-if="item.status === 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      fab
                      x-small
                      color="success"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      elevation="3"
                      :loading="loadingAction === item.id"
                      @click="changeAttendanceStudent(false, item.id)"
                    >
                      <v-icon small>mdi-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('remove_assistance') }}</span>
                </v-tooltip>

                <!-- Status diferente a -1 y 0: Mostrar check de "Ya firmado/Salida" -->
                <v-avatar v-else size="36" color="primary lighten-5" class="elevation-1">
                  <v-icon color="primary" small>mdi-check-all</v-icon>
                </v-avatar>
              </div>
            </template>

            <template v-slot:item.action_status="{ item }">
              <div class="text-center font-weight-medium">
                <span v-if="item.status === -1" class="red--text text--darken-2">
                  {{ $t('did_not_attend') }}
                </span>
                
                <v-btn
                  v-else-if="item.status === 0"
                  color="primary"
                  small
                  elevation="2"
                  class="rounded-pill px-4"
                  :loading="loadingSign === item.id"
                  @click="signExitPass(item.id)"
                >
                  <v-icon left small>mdi-pen</v-icon>
                  {{ $t('sign_exit') }}
                </v-btn>

                <span v-else-if="item.status === 1" class="success--text font-weight-bold d-flex align-center justify-center">
                  <v-icon x-small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                  {{ $t('signed') }}
                </span>

                <span v-else-if="item.status === 2" class="blue--text text--darken-2 d-flex align-center justify-center">
                  <v-icon x-small color="blue darken-2" class="mr-1">mdi-exit-run</v-icon>
                  {{ $t('out_of_school') }}
                </span>

                <span v-else-if="item.status === 3" class="orange--text text--darken-3">
                  {{ $t('transferred') }}
                </span>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <!-- <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-lighten-5">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-8 font-weight-bold rounded-pill elevation-4"
            @click="dialogStudents = false"
            large
          >
            Cerrar
          </v-btn>
        </v-card-actions> -->
      </v-card>
    </v-dialog>

    <!-- Transfer Dialog -->
    <v-dialog
      v-model="dialogTransfer"
      max-width="950px"
      scrollable
      transition="scale-transition"
    >
      <v-card class="student-list-card glass-card">
        <v-toolbar flat dark color="orange darken-3" class="px-4">
          <v-avatar color="white" size="40" class="mr-4">
            <v-icon color="orange darken-3">mdi-account-switch</v-icon>
          </v-avatar>
          <v-toolbar-title class="font-weight-black text-h5 tracking-tight">
            {{ selectedClassroom ? selectedClassroom.name : 'Transferencia' }} - 
            <span class="text-subtitle-1">Estudiantes con Actividad</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn 
            v-if="selectedTransferStudents.length > 0"
            color="success" 
            dark 
            depressed
            class="mr-4 rounded-lg font-weight-bold"
            :loading="loadingBulkTransfer"
            @click="markBulkTransfer"
          >
            <v-icon left>mdi-account-switch</v-icon>
            Transferir masivo ({{ selectedTransferStudents.length }})
          </v-btn>
          <v-btn icon @click="dialogTransfer = false" class="bg-white-opacity">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <v-data-table
            v-model="selectedTransferStudents"
            :headers="transferHeaders"
            :items="studentsTransfer"
            :loading="loadingTransfer"
            show-select
            item-key="id"
            class="elevation-0 custom-data-table"
            no-data-text="No hay estudiantes con actividad en este salón."
            loading-text="Cargando lista de transferencia..."
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.avatar="{ item }">
              <v-avatar size="50" class="my-3 elevation-4 border-2-white shadow-sm">
                <v-img :src="item.avatar || defaultAvatar" :alt="item.first_name">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0 bg-grey-lighten-4" align="center" justify="center">
                      <v-icon color="grey lighten-1" size="30">mdi-account-outline</v-icon>
                    </v-row>
                  </template>
                </v-img>
              </v-avatar>
            </template>
            
            <template v-slot:item.full_name="{ item }">
              <div class="text-body-1 font-weight-bold text-darker">{{ item.last_name + ' ' + item.first_name }}</div>
            </template>

            <template v-slot:item.extracurricular_activity="{ item }">
              <div v-if="item.extracurricular_activity">
                <v-chip
                  small
                  color="blue lighten-5"
                  text-color="blue darken-4"
                  class="ma-1 font-weight-bold"
                >
                  <v-icon left x-small>mdi-star-face</v-icon>
                  {{ item.extracurricular_activity }}
                </v-chip>
              </div>
              <div v-else-if="item.extracurricular_activities && item.extracurricular_activities.length">
                <v-chip
                  v-for="activity in item.extracurricular_activities"
                  :key="activity.id"
                  small
                  color="blue lighten-5"
                  text-color="blue darken-4"
                  class="ma-1 font-weight-bold"
                >
                  <v-icon left x-small>mdi-star-face</v-icon>
                  {{ activity.name || activity }}
                </v-chip>
              </div>
              <div v-else class="text-caption grey--text font-italic">Sin actividad asignada</div>
            </template>
          </v-data-table>
        </v-card-text>
<!-- 
        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-lighten-5">
          <v-spacer></v-spacer>
          <v-btn
            color="orange darken-3"
            dark
            class="px-8 font-weight-bold rounded-pill elevation-4"
            @click="dialogTransfer = false"
            large
          >
            Cerrar
          </v-btn>
        </v-card-actions> -->
      </v-card>
    </v-dialog>

    <v-tour name="attendanceTour" :steps="steps" :options="tourOptions"></v-tour>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'

export default {
  name: "attendance",
  data() {
    return {
      classrooms: [],
      selectedClassroom: null,
      students: [],
      loadingClassrooms: false,
      loadingStudents: false,
      loadingTransfer: false,
      loadingAction: null,
      loadingSign: null,
      loadingBulk: false,
      loadingBulkTransfer: false,
      dialogStudents: false,
      dialogTransfer: false,
      selectedStudents: [],
      selectedTransferStudents: [],
      studentsTransfer: [],
      defaultAvatar: require('@/assets/logo_escuela.png'),
      transferHeaders: [
        {
          text: this.$t('photo'),
          align: 'center',
          sortable: false,
          value: 'avatar',
          width: '100px'
        },
        {
          text: this.$t('Apellido') + ' ' + this.$t('Nombre'),
          align: 'left',
          sortable: true,
          value: 'full_name',
        },
        {
          text: 'Actividades Extracurriculares',
          align: 'left',
          sortable: false,
          value: 'extracurricular_activity',
        },
        {
          text: '',
          value: 'data-table-select',
          sortable: false,
          width: '60px'
        }
      ],
      studentHeaders: [
        {
          text: this.$t('photo'),
          align: 'center',
          sortable: false,
          value: 'avatar',
          width: '120px'
        },
        {
          text: this.$t('Apellido'),
          align: 'left',
          sortable: true,
          value: 'last_name',
          class: 'text-overline'
        },
        {
          text: this.$t('Nombre'),
          align: 'left',
          sortable: true,
          value: 'first_name',
          class: 'text-overline'
        },
        {
          text: this.$t('attended'),
          align: 'center',
          sortable: false,
          value: 'status',
          width: '140px'
        },
        {
          text: this.$t('estado'),
          align: 'center',
          sortable: true,
          value: 'action_status',
          width: '160px'
        }
      ],
      tourOptions: {
        useKeyboardNavigation: false,
        labels: {
          buttonSkip: 'Omitir tour',
          buttonPrevious: 'Anterior',
          buttonNext: 'Siguiente',
          buttonStop: 'Finalizar'
        }
      },
      steps: [
        {
          target: '[data-v-step="0"]',
          header: {
            title: 'Lista de Asistencia',
          },
          content: 'Aquí podrás visualizar todos tus salones asignados para pasar lista o consultar a los estudiantes. Puedes omitir este tour en cualquier momento.'
        },
        {
          target: '[data-v-step="1"]',
          header: {
            title: 'Pasar Lista',
          },
          content: 'Cada tarjeta representa un salón. Haz clic en <strong>Pasar lista</strong> para ver los estudiantes o tomar asistencia masivamente.'
        },
        {
          target: '[data-v-step="2"]',
          header: {
            title: 'Transferencias',
          },
          content: 'Utiliza el botón <strong>Transferir</strong> para enviar a un estudiante a una actividad extracurricular o ver los que están asignados.'
        }
      ]
    }
  },
  computed: {
    language() {
      return store.state.language
    },
    showBulkSelection() {
      return this.students.length > 0 && this.students.every(s => s.status === -1);
    },
    computedStudentHeaders() {
      if (this.showBulkSelection) {
        const headers = this.studentHeaders.filter(h => h.value !== 'status' && h.value !== 'action_status');
        headers.push({
          text: '',
          value: 'data-table-select',
          sortable: false,
          width: '60px'
        });
        return headers;
      }
      return this.studentHeaders;
    }
  },
  mounted() {
    setTimeout(() => {
      if (!localStorage.getItem('attendanceTourCompleted') && this.classrooms.length > 0) {
        this.$tours['attendanceTour'].start()
        localStorage.setItem('attendanceTourCompleted', 'true')
      }
    }, 1000);
  },
  created() {
    this.getClassrooms()
  },
  methods: {
    getClassrooms() {
      this.loadingClassrooms = true
      HTTP.get('classroom/', {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(response => {
        this.classrooms = response.data
        this.loadingClassrooms = false
      })
      .catch(error => {
        console.error('Error fetching classrooms:', error)
        this.loadingClassrooms = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'No se pudieron cargar los salones.')
        setTimeout(this.clear, 3000)
      })
    },
    showStudents(classroom) {
      this.selectedClassroom = classroom
      this.dialogStudents = true
      this.getStudents(classroom.id)
    },
    getStudents(classroomId) {
      this.loadingStudents = true
      this.students = []
      this.selectedStudents = [] // Limpiar seleccion al recargar
      HTTP.get(`classroom/${classroomId}/students/`, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(response => {
        this.students = response.data
        this.loadingStudents = false
      })
      .catch(error => {
        console.error('Error fetching students:', error)
        this.loadingStudents = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al obtener la lista de alumnos.')
        setTimeout(this.clear, 3000)
      })
    },
    markBulkAttendance() {
      if (this.selectedStudents.length === 0) return
      
      this.loadingBulk = true
      const payload = this.selectedStudents.map(s => ({ 'idStudent': s.id }))
      
      HTTP.post(`classroom/${this.selectedClassroom.id}/list/take/`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(() => {
        this.getStudents(this.selectedClassroom.id)
        this.loadingBulk = false
        this.selectedStudents = []
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Asistencia masiva registrada exitosamente.')
        setTimeout(this.clear, 3000)
      })
      .catch(error => {
        console.error('Error in bulk attendance:', error)
        this.loadingBulk = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al registrar asistencia masiva.')
        setTimeout(this.clear, 3000)
      })
    },
    changeAttendanceStudent(take, studentId) {
      this.loadingAction = studentId
      const aux = [{ 'idStudent': studentId }]
      
      if (take) {
        HTTP.post(`classroom/${this.selectedClassroom.id}/list/take/`, aux, {
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          }
        })
        .then(() => {
          this.getStudents(this.selectedClassroom.id)
          this.loadingAction = null
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('attendance_list_sent_success'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          console.error('Error taking attendance:', error)
          this.loadingAction = null
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Error al registrar la asistencia.')
          setTimeout(this.clear, 3000)
        })
      } else {
        HTTP.delete(`classroom/${this.selectedClassroom.id}/list/remove/`, {
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          data: aux
        })
        .then(() => {
          this.getStudents(this.selectedClassroom.id)
          this.loadingAction = null
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Asistencia removida correctamente.')
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          console.error('Error removing attendance:', error)
          this.loadingAction = null
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Error al remover la asistencia.')
          setTimeout(this.clear, 3000)
        })
      }
    },
    signExitPass(uuidStudent) {
      this.loadingSign = uuidStudent
      const student = [{ 'idStudent': uuidStudent }]
      
      HTTP.patch(`classroom/${this.selectedClassroom.id}/list/sign_pass/`, student, {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(() => {
        this.loadingSign = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', this.$t('sign_pass_success'))
        this.getStudents(this.selectedClassroom.id)
        setTimeout(this.clear, 3000)
      })
      .catch(error => {
        console.error('Error signing exit pass:', error)
        this.loadingSign = null
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al firmar el pase de salida.')
        setTimeout(this.clear, 3000)
      })
    },
    markBulkTransfer() {
      if (this.selectedTransferStudents.length === 0) return
      
      this.loadingBulkTransfer = true
      const payload = {
        students: this.selectedTransferStudents.map(s => s.id)
      }
      
      HTTP.post('transferred-extracurricular/', payload, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(() => {
        this.loadingBulkTransfer = false
        this.selectedTransferStudents = []
        this.dialogTransfer = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Transferencia masiva realizada con éxito.')
        // Actualizar la lista general si el diálogo de estudiantes estaba abierto o para reflejar cambios
        if (this.selectedClassroom) {
          this.getStudents(this.selectedClassroom.id)
        }
        setTimeout(this.clear, 3000)
      })
      .catch(error => {
        console.error('Error in bulk transfer:', error)
        this.loadingBulkTransfer = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al realizar la transferencia masiva.')
        setTimeout(this.clear, 3000)
      })
    },
    transferClassroom(classroom) {
      this.selectedClassroom = classroom
      this.dialogTransfer = true
      this.selectedTransferStudents = []
      this.getStudentsWithActivity(classroom.id)
    },
    getStudentsWithActivity(classroomId) {
      this.loadingTransfer = true
      this.studentsTransfer = []
      HTTP.get(`classroom/${classroomId}/students_with_activity/`, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
      .then(response => {
        // Filtrar solamente los estudiantes con status == 0
        this.studentsTransfer = response.data.filter(student => student.status === 0)
        this.loadingTransfer = false
      })
      .catch(error => {
        console.error('Error fetching transfer students:', error)
        this.loadingTransfer = false
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'No se pudieron cargar los estudiantes con actividad.')
        setTimeout(this.clear, 3000)
      })
    },
    clear() {
      this.$store.commit('snackbar_activate_false')
    },
    getGradientClass(index) {
      const gradients = ['grad-1', 'grad-2', 'grad-3', 'grad-4', 'grad-5', 'grad-6'];
      return gradients[index % gradients.length];
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap');

.bg-light-gradient {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif !important;
}

.tracking-tight {
  letter-spacing: -0.05em;
}

.custom-divider {
  width: 80px;
  border-width: 3px;
  border-radius: 5px;
  border-color: var(--v-primary-base) !important;
  opacity: 1;
}

.classroom-card {
  cursor: pointer;
  position: relative;
  border: none;
  background: white;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.on-hover {
  transform: translateY(-8px);
}

.card-header-gradient {
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.classroom-icon-floating {
  font-size: 60px !important;
  opacity: 0.3;
  transform: rotate(-15deg);
  position: absolute;
  right: -10px;
  bottom: -10px;
}

.grad-1 { background: linear-gradient(45deg, #FF6B6B 0%, #FF8E53 100%); }
.grad-2 { background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%); }
.grad-3 { background: linear-gradient(45deg, #667eea 0%, #764ba2 100%); }
.grad-4 { background: linear-gradient(45deg, #43e97b 0%, #38f9d7 100%); }
.grad-5 { background: linear-gradient(45deg, #fa709a 0%, #fee140 100%); }
.grad-6 { background: linear-gradient(45deg, #30cfd0 0%, #330867 100%); }

.gradient-toolbar {
  background: linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%) !important;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
}

.bg-white-opacity {
  background: rgba(255, 255, 255, 0.1);
}

.border-2-white {
  border: 4px solid white;
}

.shadow-sm {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.custom-data-table >>> thead th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.custom-data-table >>> tbody tr:hover {
  background-color: #f1f5f9 !important;
}

.text-dark { color: #1e293b; }
.text-darker { color: #0f172a; }

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
}

.transition-all-slow {
  transition: all 0.5s ease;
}

.empty-state-container {
  border: 2px dashed #e2e8f0;
}
</style>
